import {
  Asset,
  AssetId,
  assetVault,
  PERSONALIZATION_NEGATIVE_ACTIONED_REDIRECTION_DELAY_MS,
  PERSONALIZATION_POSITIVE_ACTIONED_REDIRECTION_DELAY_MS,
  PERSONALIZATION_REDIRECTION_DELAY_MS,
  PostCheckoutEligibility,
  PostCheckoutFlow,
  postCheckoutFlowSteps,
} from '../model'
import { Dispatch, SetStateAction, useState, useEffect } from 'react'
import { useConsumerTag, usePostCheckoutEligibility, usePostCheckoutExperiment, usePostCheckoutFlow, useTags } from '../state'
import { useRedirectionDelay } from './redirects'
import { sanitizeTagData } from '@afterpaytouch/portal-api/consumer/tags'
import { getCtaTagName, getImpressionsTagName, getAssetImpressions } from './post-checkout'

interface PostCheckout {
  asset: Asset
  eligibility: PostCheckoutEligibility
  enabled: boolean
  flow: PostCheckoutFlow
  onAssetImpression: () => void
  onAssetNegativeActionClick: (value: string) => number
  onAssetPositiveActionClick: (value: string) => number
  showComponentState: { showComponent: boolean; setShowComponent: Dispatch<SetStateAction<boolean>> }
  stepState: { step: number; setStep: Dispatch<SetStateAction<number>> }
  timeout: number | null
}
interface Personalization {
  postCheckout: PostCheckout
}

export const usePersonalization = (): Personalization => {
  const tags = useConsumerTag()
  const [_, saveTag] = useTags()
  const { enabled: isPostCheckoutExperimentEnabled, experiment } = usePostCheckoutExperiment()
  const id = experiment.assetId ?? AssetId.Survey202303
  const [cachedAssetId, setCachedAssetId] = useState(id)
  const asset = assetVault[cachedAssetId]
  const postCheckoutEligible = usePostCheckoutEligibility(asset)
  const flow = usePostCheckoutFlow()
  const [step, setStep] = useState(1)
  const totalSteps = postCheckoutFlowSteps[flow]
  const postCheckoutEnabled = postCheckoutEligible.result && isPostCheckoutExperimentEnabled
  const [showComponent, setShowComponent] = useState(postCheckoutEnabled && step / totalSteps === 1)

  const isPostCaptureFlow = flow === PostCheckoutFlow.PostCapture

  const ctaTagName = getCtaTagName(cachedAssetId)
  const impressionsTagName = getImpressionsTagName(cachedAssetId)

  const redirectionDelay = useRedirectionDelay()

  const onAssetImpression = (): void => {
    const impressionCount = getAssetImpressions(cachedAssetId, tags)
    saveTag(impressionsTagName, `${impressionCount + 1}`)
  }

  const onAssetPositiveActionClick = (value: string): number => {
    saveTag(ctaTagName, value)
    return PERSONALIZATION_POSITIVE_ACTIONED_REDIRECTION_DELAY_MS
  }

  const onAssetNegativeActionClick = (value: string): number => {
    saveTag(ctaTagName, sanitizeTagData(value, `${id}-not-interested`))
    return PERSONALIZATION_NEGATIVE_ACTIONED_REDIRECTION_DELAY_MS
  }

  const getTimeout = (isEligible: boolean, redirectionDelay: number): number | null => {
    if (!isEligible) {
      return redirectionDelay
    }

    if (isPostCaptureFlow) {
      return null
    }

    return asset.redirectionDelay ?? PERSONALIZATION_REDIRECTION_DELAY_MS
  }

  useEffect(() => {
    if (postCheckoutEnabled && experiment.assetId) {
      setCachedAssetId(id)
    }
  }, [postCheckoutEnabled, id, experiment.assetId])

  return {
    postCheckout: {
      timeout: getTimeout(postCheckoutEnabled, redirectionDelay),
      enabled: postCheckoutEnabled,
      eligibility: postCheckoutEligible,
      asset,
      onAssetImpression,
      onAssetPositiveActionClick,
      onAssetNegativeActionClick,
      flow,
      showComponentState: {
        showComponent,
        setShowComponent,
      },
      stepState: {
        setStep,
        step,
      },
    },
  }
}
